import { getAbsoluteEndpoint, getTaxonomyPath } from './apiRouting'
import { getTaxonomyCache, saveTaxonomyCache, type TaxonomyCacheRecord } from './db'
import type { TaxonomyPayload } from '@/types/taxonomy'
import { buildProtectedAuthHeaders } from './protectedNetwork'
import { isAuthExpiredError } from './authErrors'
import { throwIfAuthRejected } from './api/client'

async function fetchRemoteTaxonomy(
  url: string,
  headers: HeadersInit,
  signal?: AbortSignal,
): Promise<TaxonomyPayload> {
  const response = await fetch(url, {
    headers,
    signal,
  })

  if (!response.ok) {
    // Only 401 means re-authenticate. Any other status (including a 403
    // "forbidden") is treated as a load failure and falls back to cache rather
    // than signing the user out.
    throwIfAuthRejected(response, 'load taxonomy')
    throw new Error(`Failed to load taxonomy: ${response.status} ${response.statusText}`)
  }

  const payload = (await response.json()) as Partial<TaxonomyPayload>

  if (!payload.assets || !payload.indications || !payload.keyInsightQuestions) {
    throw new Error('Taxonomy payload missing required fields')
  }

  return {
    version: payload.version ?? null,
    updatedAt: payload.updatedAt ?? new Date().toISOString(),
    assets: payload.assets,
    indications: payload.indications,
    keyInsightQuestions: payload.keyInsightQuestions,
    insightTypesEnabled: payload.insightTypesEnabled ?? false,
  }
}

async function loadStaticFallback(): Promise<TaxonomyPayload> {
  const module = await import('@/assets/taxonomy.json')
  const now = new Date().toISOString()
  return {
    version: 'static-fallback',
    updatedAt: now,
    assets: module.default.assets,
    indications: module.default.indications,
    keyInsightQuestions: module.default.keyInsightQuestions,
    insightTypesEnabled: false,
  }
}

export interface TaxonomyFetchResult {
  payload: TaxonomyPayload
  fromCache: boolean
}

export async function getCachedTaxonomy(): Promise<TaxonomyCacheRecord | null> {
  return getTaxonomyCache()
}

export async function fetchAndCacheTaxonomy(): Promise<TaxonomyFetchResult> {
  let payload: TaxonomyPayload | null = null
  let fromCache = false
  try {
    const headers = await buildProtectedAuthHeaders('load taxonomy', {
      Accept: 'application/json',
    })
    const taxonomyUrl = getAbsoluteEndpoint('taxonomyRead', getTaxonomyPath)
    payload = await fetchRemoteTaxonomy(taxonomyUrl, headers)
  } catch (error) {
    if (isAuthExpiredError(error)) {
      throw error
    }

    console.warn('Remote taxonomy fetch failed, attempting fallback', error)
    const cache = await getTaxonomyCache()
    if (cache) {
      return {
        payload: cache.payload,
        fromCache: true,
      }
    }
    payload = await loadStaticFallback()
    fromCache = true
  }

  if (!payload) {
    throw new Error('Taxonomy payload unavailable after fetch/fallback attempts')
  }

  await saveTaxonomyCache({
    version: payload.version,
    updatedAt: payload.updatedAt,
    payload,
    cachedAt: new Date().toISOString(),
  })

  return {
    payload,
    fromCache,
  }
}
